import { useEffect, useRef, useState } from "react";
import { Zap, Hand, Footprints, Music2, Star, Play, Pause, Volume2 } from "lucide-react";
import SectionHeading, { Reveal } from "./SectionHeading";

const BAT_IMG =
  "https://images.unsplash.com/photo-1723586762109-12527019b6a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxiYXNlYmFsbCUyMGJhdCUyMG5lb258ZW58MHx8fHwxNzg3Nzk3MDg1fDA&ixlib=rb-4.1.0&q=85";

const ICONS = {
  zap: Zap,
  hand: Hand,
  footprints: Footprints,
};

const pad = (n) => String(n).padStart(2, "0");

const Equalizer = () => (
  <span className="flex h-4 items-end gap-[3px]" aria-hidden>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="eq-bar w-[3px] rounded-full bg-neon"
        style={{ height: "100%", animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </span>
);

export default function Dugout({ gear, walkupSongs }) {
  const featured = gear.find((g) => g.featured) || gear[0];
  const rest = gear.filter((g) => g !== featured);
  const FeaturedIcon = ICONS[featured.icon] || Star;

  const [playing, setPlaying] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const audioRef = useRef(null);
  const ytPlayers = useRef({});
  const ytReady = useRef(null);

  const stopAll = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    Object.values(ytPlayers.current).forEach((p) => {
      try {
        p.pauseVideo();
      } catch {}
    });
    setPlaying(-1);
    setProgress(0);
  };

  const loadYT = () => {
    if (!ytReady.current) {
      ytReady.current = new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve();
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (prev) prev();
          resolve();
        };
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      });
    }
    return ytReady.current;
  };

  const toggle = async (i, s) => {
    if (playing === i) {
      stopAll();
      return;
    }
    stopAll();
    if (s.previewUrl) {
      const a = new Audio(s.previewUrl);
      a.volume = volume;
      a.ontimeupdate = () => setProgress(a.currentTime / (a.duration || 30));
      a.onended = () => {
        setPlaying(-1);
        setProgress(0);
      };
      a.play().catch(() => {});
      audioRef.current = a;
      setPlaying(i);
    } else if (s.youtubeId) {
      await loadYT();
      let p = ytPlayers.current[i];
      if (!p) {
        p = new window.YT.Player(`yt-hidden-${i}`, {
          videoId: s.youtubeId,
          height: "0",
          width: "0",
          playerVars: { rel: 0 },
          events: {
            onReady: (e) => {
              e.target.setVolume(Math.round(volume * 100));
              e.target.playVideo();
            },
            onStateChange: (e) => {
              if (e.data === window.YT.PlayerState.ENDED) setPlaying(-1);
            },
          },
        });
        ytPlayers.current[i] = p;
      } else {
        p.setVolume(Math.round(volume * 100));
        p.playVideo();
      }
      setPlaying(i);
    }
  };

  const changeVolume = (v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    Object.values(ytPlayers.current).forEach((p) => {
      try {
        p.setVolume(Math.round(v * 100));
      } catch {}
    });
  };

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause();
      Object.values(ytPlayers.current).forEach((p) => {
        try {
          p.destroy();
        } catch {}
      });
    },
    []
  );

  return (
    <section
      id="dugout"
      data-testid="section-dugout"
      className="mx-auto max-w-7xl px-6 py-24 md:py-36"
    >
      <div className="relative">
        <SectionHeading
          number="03"
          title="THE DUGOUT"
          sub="Gear that gets the job done"
          id="dugout"
        />
        <img
          src="/images/hat-cutout.webp"
          alt="Big Cal's All-Star hat"
          data-testid="dugout-hat"
          className="absolute -top-16 right-2 hidden w-40 -rotate-12 drop-shadow-[0_12px_35px_rgba(57,255,20,0.3)] transition-transform duration-500 hover:-rotate-3 md:block lg:w-48"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Reveal className="md:row-span-2">
          <div
            data-testid="gear-featured"
            className="group relative flex h-full min-h-[380px] flex-col justify-end overflow-hidden rounded-2xl border border-white/10 transition-colors duration-300 hover:border-neon"
          >
            <img
              src={BAT_IMG}
              alt={featured.name}
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative p-7">
              <p className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-[0.3em] text-neon">
                <FeaturedIcon className="h-3.5 w-3.5" />
                {featured.type}
              </p>
              <p className="mt-3 font-bebas text-4xl leading-none tracking-wide">
                {featured.name}
              </p>
              <p className="mt-2 text-sm text-white/60">{featured.detail}</p>
            </div>
          </div>
        </Reveal>

        {rest.map((g, i) => {
          const Icon = ICONS[g.icon] || Star;
          return (
            <Reveal key={g.type} delay={0.08 * (i + 1)}>
              <div
                data-testid={`gear-${g.type.toLowerCase().replaceAll(" ", "-")}`}
                className="group h-full rounded-2xl border border-white/10 bg-panel p-7 transition-colors duration-300 hover:border-neonpurple"
              >
                <p className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-[0.3em] text-neonpurple">
                  <Icon className="h-3.5 w-3.5" />
                  {g.type}
                </p>
                <p className="mt-4 font-bebas text-3xl leading-none tracking-wide">
                  {g.name}
                </p>
                <p className="mt-2 text-sm text-white/60">{g.detail}</p>
              </div>
            </Reveal>
          );
        })}

        <Reveal delay={0.15} className="md:col-span-2">
          <div
            data-testid="walkup-songs"
            className="h-full rounded-2xl border border-white/10 bg-panel transition-colors duration-300 hover:border-hotpink"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-7 py-4">
              <p className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-[0.3em] text-hotpink">
                <Music2 className="h-3.5 w-3.5" />
                Walk-Up Songs
              </p>
              <div className="flex items-center gap-3">
                <Volume2 className="h-3.5 w-3.5 text-white/40" />
                <input
                  data-testid="walkup-volume"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  className="w-24 accent-[#39FF14]"
                  aria-label="Volume"
                />
              </div>
            </div>
            <ol>
              {walkupSongs.map((s, i) => (
                <li
                  key={s.title}
                  data-testid={`walkup-song-${i + 1}`}
                  className="group relative border-b border-white/5 px-7 py-4 transition-colors duration-300 last:border-b-0 hover:bg-white/[0.03]"
                >
                  <div className="flex items-center gap-5">
                    <button
                      data-testid={`walkup-play-${i + 1}`}
                      onClick={() => toggle(i, s)}
                      aria-label={`Play ${s.title}`}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                        playing === i
                          ? "border-neon bg-neon text-black shadow-[0_0_20px_rgba(57,255,20,0.45)]"
                          : "border-white/20 text-white/70 hover:border-neon hover:text-neon"
                      }`}
                    >
                      {playing === i ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="ml-0.5 h-4 w-4" />
                      )}
                    </button>
                    {(s.artwork || s.youtubeId) && (
                      <img
                        src={
                          s.artwork ||
                          `https://i.ytimg.com/vi/${s.youtubeId}/hqdefault.jpg`
                        }
                        alt=""
                        className={`h-11 w-11 shrink-0 rounded-lg border border-white/10 object-cover transition-shadow duration-300 ${
                          playing === i
                            ? "shadow-[0_0_18px_rgba(57,255,20,0.35)]"
                            : ""
                        }`}
                      />
                    )}
                    <div className="min-w-0">
                      <p
                        className={`truncate font-bebas text-2xl leading-tight tracking-wide transition-colors duration-300 ${
                          playing === i ? "text-neon" : "group-hover:text-neon"
                        }`}
                      >
                        {s.title}
                      </p>
                      <p className="font-jbmono text-[11px] tracking-[0.15em] text-white/40">
                        {s.artist}
                      </p>
                    </div>
                    <span className="ml-auto flex items-center gap-3">
                      {playing === i && <Equalizer />}
                      <span className="font-jbmono text-xs text-neon/70">
                        {pad(i + 1)}
                      </span>
                    </span>
                  </div>
                  {s.previewUrl && playing === i && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-white/10">
                      <div
                        data-testid={`walkup-progress-${i + 1}`}
                        className="h-full bg-neon transition-[width] duration-300"
                        style={{ width: `${Math.min(100, progress * 100)}%` }}
                      />
                    </div>
                  )}
                  <div id={`yt-hidden-${i}`} className="hidden" />
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
